import {Table, Tag, Alert, Row, Col} from "antd";
import {Link} from "react-router-dom";
import dayjs from "dayjs";
import {PageHeader, Loading, FontAwesomeIcon, ExportButtons} from "~/components";
import {useGetAdminStatsQuery} from "~/reduxs/api/adminStatsApiSlice";
import {nf, compact, planColor} from "../util";
import style from "../style/Member.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

/** Thẻ chỉ số nhỏ (tái dùng lớp .stat của Member.module.scss). */
function StatCard({icon, value, label}) {
	return (
		<div className={cn('stat')}>
			<div className={cn('stat-icon')}><FontAwesomeIcon icon={icon} /></div>
			<div className={cn('stat-body')}>
				<div className={cn('stat-value')}>{value}</div>
				<div className={cn('stat-label')}>{label}</div>
			</div>
		</div>
	);
}

function AdminStats() {
	const {data, isLoading, isError} = useGetAdminStatsQuery();

	if (isError) {
		return (
			<div className="container">
				<PageHeader icon="fa-light fa-chart-line" title="Thống kê tổng quan" />
				<Alert type="error" showIcon message="Không tải được số liệu thống kê." />
			</div>
		);
	}

	if (isLoading || !data) {
		return (
			<div className="container">
				<PageHeader icon="fa-light fa-chart-line" title="Thống kê tổng quan" />
				<Loading />
			</div>
		);
	}

	const {members, plans, resources, ai_tokens, url_checks, top_token_users, top_project_users, alerts} = data;

	// Dữ liệu xuất báo cáo (Excel/PDF) — gate theo cờ gói ở ExportButtons.
	const overviewRows = [
		['Tổng thành viên', nf(members.total)],
		['Đang hoạt động', nf(members.active)],
		['Bị khóa / tạm dừng', nf(members.blocked)],
		['Mới trong tháng', nf(members.new_this_month)],
		['Doanh thu ước tính / tháng', `${nf(plans?.revenue_monthly)}đ`],
		['Token AI dùng trong tháng', nf(ai_tokens.used_this_month)],
		['Kiểm tra URL trong tuần', nf(url_checks.used_this_week)],
		['Tổng dự án', nf(resources.projects)],
	];
	const planRows = (plans?.items || []).map((p) => [p.name, p.price > 0 ? `${nf(p.price)}đ` : 'Miễn phí', nf(p.subscribers), `${nf(p.revenue)}đ`]);
	const buildExcel = () => [
		{name: 'Tổng quan', aoa: [['Chỉ số', 'Giá trị'], ...overviewRows]},
		{name: 'Gói', aoa: [['Gói', 'Giá', 'Người dùng', 'Doanh thu'], ...planRows]},
		{name: 'Top token', aoa: [['Thành viên', 'Token'], ...(top_token_users || []).map((r) => [r.name, r.value])]},
		{name: 'Top dự án', aoa: [['Thành viên', 'Dự án'], ...(top_project_users || []).map((r) => [r.name, r.value])]},
	];
	const buildPdf = () => ({
		title: 'Thống kê tổng quan hệ thống',
		subtitle: `Cập nhật ${dayjs(data.generated_at).format('HH:mm DD/MM/YYYY')}`,
		sections: [
			{heading: 'Tổng quan', columns: ['Chỉ số', 'Giá trị'], rows: overviewRows},
			{heading: 'Gói dịch vụ', columns: ['Gói', 'Giá', 'Người dùng', 'Doanh thu'], rows: planRows},
		],
	});

	const planColumns = [
		{title: 'Gói', key: 'name', render: (_, r) => (
			<span><Tag color={planColor(r.slug)}>{r.name}</Tag>{r.status === 'inactive' && <Tag>ẩn</Tag>}</span>
		)},
		{title: 'Giá / kỳ', dataIndex: 'price', key: 'price', align: 'right', render: (v) => v > 0 ? `${nf(v)}đ` : 'Miễn phí'},
		{title: 'Người dùng', dataIndex: 'subscribers', key: 'subscribers', align: 'center', render: (v) => nf(v)},
		{title: 'Doanh thu ước tính', dataIndex: 'revenue', key: 'revenue', align: 'right', render: (v) => `${nf(v)}đ`},
	];

	const topColumns = (label) => [
		{title: 'Thành viên', key: 'name', render: (_, r) => (
			<Link to={`/admin/members/${r.id}`}>{r.name}</Link>
		)},
		{title: label, dataIndex: 'value', key: 'value', align: 'right', render: (v) => nf(v)},
	];

	return (
		<div className="container">
			<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap'}}>
				<PageHeader
					icon="fa-light fa-chart-line"
					title="Thống kê tổng quan"
					subtitle={`Bức tranh toàn hệ thống · cập nhật ${dayjs(data.generated_at).format('HH:mm DD/MM/YYYY')}`}
				/>
				<ExportButtons filename="thong-ke-tong-quan" buildExcel={buildExcel} buildPdf={buildPdf} small={false} />
			</div>

			{/* Cảnh báo bất thường */}
			{alerts?.login_fail_24h > 0 && (
				<Alert
					type="warning"
					showIcon
					style={{marginBottom: 16}}
					message={`${nf(alerts.login_fail_24h)} lượt đăng nhập thất bại trong 24 giờ qua (${nf(alerts.login_fail_7d)} trong 7 ngày). Kiểm tra dấu hiệu dò mật khẩu.`}
				/>
			)}

			{/* Thành viên */}
			<div className={cn('section-title')}>Thành viên</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-users" value={nf(members.total)} label="Tổng thành viên" />
				<StatCard icon="fa-light fa-user-check" value={nf(members.active)} label="Đang hoạt động" />
				<StatCard icon="fa-light fa-user-lock" value={nf(members.blocked)} label="Bị khóa / tạm dừng" />
				<StatCard icon="fa-light fa-user-plus" value={nf(members.new_this_month)} label="Mới trong tháng" />
			</div>

			{/* Doanh thu & Gói */}
			<div className={cn('section-title')}>Doanh thu & Gói dịch vụ</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-sack-dollar" value={`${nf(plans?.revenue_monthly)}đ`} label="Doanh thu ước tính / tháng" />
			</div>
			<Table
				columns={planColumns}
				dataSource={plans?.items || []}
				rowKey="slug"
				pagination={false}
				size="middle"
				scroll={{x: 'max-content'}}
				style={{marginBottom: 24}}
			/>

			{/* Tài nguyên hệ thống */}
			<div className={cn('section-title')}>Tài nguyên hệ thống</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-folder-tree" value={nf(resources.projects)} label="Dự án" />
				<StatCard icon="fa-light fa-magnifying-glass-chart" value={nf(resources.seo_reports)} label="Báo cáo SEO" />
				<StatCard icon="fa-light fa-gauge-high" value={nf(resources.psi_reports)} label="Báo cáo PageSpeed" />
				<StatCard icon="fa-light fa-spider" value={nf(resources.crawls)} label="Lượt crawl" />
			</div>

			{/* Token & URL */}
			<div className={cn('section-title')}>AI Token & Kiểm tra URL</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-wand-magic-sparkles" value={compact(ai_tokens.used_this_month)} label="Token AI dùng trong tháng" />
				<StatCard icon="fa-light fa-coins" value={compact(ai_tokens.consumed_all_time)} label="Tổng token đã tiêu" />
				<StatCard icon="fa-light fa-link" value={nf(url_checks.used_this_week)} label="Kiểm tra URL trong tuần" />
				<StatCard icon="fa-light fa-robot" value={nf(resources.ai_history)} label="Lượt sinh nội dung AI" />
			</div>

			{/* Top thành viên */}
			<div className={cn('section-title')}>Top thành viên dùng nhiều tài nguyên</div>
			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<Table
						title={() => <strong>Nhiều token AI nhất (tháng này)</strong>}
						columns={topColumns('Token')}
						dataSource={top_token_users || []}
						rowKey="id"
						pagination={false}
						size="small"
						locale={{emptyText: 'Chưa có dữ liệu'}}
					/>
				</Col>
				<Col xs={24} lg={12}>
					<Table
						title={() => <strong>Nhiều dự án nhất</strong>}
						columns={topColumns('Dự án')}
						dataSource={top_project_users || []}
						rowKey="id"
						pagination={false}
						size="small"
						locale={{emptyText: 'Chưa có dữ liệu'}}
					/>
				</Col>
			</Row>
		</div>
	);
}

export default AdminStats;
